/**
 * 增量式 SM3（GM/T 0004-2012）实现
 *
 * sm-crypto 只提供一次性 sm3(data)，大文件需把全部内容一次性载入内存并在主线程同步运算，
 * 会导致内存暴涨与页面卡死。这里提供 update/digest 流式接口，
 * 配合分片读取即可在不阻塞的前提下计算任意大小文件的摘要。
 */

const IV = [0x7380166f, 0x4914b2b9, 0x172442d7, 0xda8a0600, 0xa96f30bc, 0x163138aa, 0xe38dee4d, 0xb0fb0e4e];

// T_j = ROTL(j < 16 ? 0x79cc4519 : 0x7a879d8a, j)，j 为轮数
const T = new Uint32Array(64);
for (let j = 0; j < 64; j++) {
  T[j] = rotl(j < 16 ? 0x79cc4519 : 0x7a879d8a, j);
}

function rotl(x: number, n: number): number {
  return ((x << n) | (x >>> (32 - n))) >>> 0;
}

function ff(x: number, y: number, z: number, j: number): number {
  return j < 16 ? (x ^ y ^ z) >>> 0 : ((x & y) | (x & z) | (y & z)) >>> 0;
}

function gg(x: number, y: number, z: number, j: number): number {
  return j < 16 ? (x ^ y ^ z) >>> 0 : ((x & y) | (~x & z)) >>> 0;
}

function p0(x: number): number {
  return (x ^ rotl(x, 9) ^ rotl(x, 17)) >>> 0;
}

function p1(x: number): number {
  return (x ^ rotl(x, 15) ^ rotl(x, 23)) >>> 0;
}

/** SM3 增量计算器，用法：new SM3().update(chunk).update(chunk).digest() */
export class SM3 {
  private v = new Uint32Array(IV);
  private buffer = new Uint8Array(64);
  private bufferLength = 0;
  private totalLength = 0;

  /** 追加一段数据，可多次调用 */
  update(data: Uint8Array): this {
    let offset = 0;
    this.totalLength += data.length;

    // 先补齐上一轮残留，凑满一个 64 字节块
    if (this.bufferLength > 0) {
      const need = 64 - this.bufferLength;
      const take = Math.min(need, data.length - offset);
      this.buffer.set(data.subarray(offset, offset + take), this.bufferLength);
      this.bufferLength += take;
      offset += take;
      if (this.bufferLength === 64) {
        this.compress(this.buffer);
        this.bufferLength = 0;
      }
    }

    // 主体部分：直接在原数组上按块压缩，避免额外拷贝
    while (offset + 64 <= data.length) {
      this.compress(data.subarray(offset, offset + 64));
      offset += 64;
    }

    // 尾部不足一块的留存到下次
    if (offset < data.length) {
      this.buffer.set(data.subarray(offset), 0);
      this.bufferLength = data.length - offset;
    }

    return this;
  }

  /** 输出 64 位小写十六进制摘要（与 sm-crypto 的 sm3() 输出一致） */
  digest(): string {
    const bitLenHi = Math.floor(this.totalLength / 0x20000000);
    const bitLenLo = (this.totalLength * 8) >>> 0;

    // 填充：数据 + 0x80 + 0x00... + 8 字节大端比特长度
    const tail = new Uint8Array(this.bufferLength + 1 + 72);
    tail.set(this.buffer.subarray(0, this.bufferLength), 0);
    tail[this.bufferLength] = 0x80;

    // 填充后总长为 64 的倍数，且末尾 8 字节留给长度字段
    const padLen = (Math.floor((this.bufferLength + 8) / 64) + 1) * 64;
    const msg = tail.subarray(0, padLen);
    const dv = new DataView(msg.buffer, msg.byteOffset, msg.byteLength);
    dv.setUint32(padLen - 8, bitLenHi);
    dv.setUint32(padLen - 4, bitLenLo);

    for (let i = 0; i < msg.length; i += 64) {
      this.compress(msg.subarray(i, i + 64));
    }

    return Array.from(this.v)
      .map((x) => x.toString(16).padStart(8, '0'))
      .join('');
  }

  private compress(block: Uint8Array): void {
    const dv = new DataView(block.buffer, block.byteOffset, block.byteLength);
    const w = new Uint32Array(68);
    const w1 = new Uint32Array(64);

    for (let j = 0; j < 16; j++) {
      w[j] = dv.getUint32(j * 4);
    }
    for (let j = 16; j < 68; j++) {
      w[j] = (p1(w[j - 16] ^ w[j - 9] ^ rotl(w[j - 3], 15)) ^ rotl(w[j - 13], 7) ^ w[j - 6]) >>> 0;
    }
    for (let j = 0; j < 64; j++) {
      w1[j] = (w[j] ^ w[j + 4]) >>> 0;
    }

    let [a, b, c, d, e, f, g, h] = this.v;

    for (let j = 0; j < 64; j++) {
      const ss1 = rotl((rotl(a, 12) + e + T[j]) >>> 0, 7);
      const ss2 = (ss1 ^ rotl(a, 12)) >>> 0;
      const tt1 = (ff(a, b, c, j) + d + ss2 + w1[j]) >>> 0;
      const tt2 = (gg(e, f, g, j) + h + ss1 + w[j]) >>> 0;
      d = c;
      c = rotl(b, 9);
      b = a;
      a = tt1;
      h = g;
      g = rotl(f, 19);
      f = e;
      e = p0(tt2);
    }

    this.v[0] = (this.v[0] ^ a) >>> 0;
    this.v[1] = (this.v[1] ^ b) >>> 0;
    this.v[2] = (this.v[2] ^ c) >>> 0;
    this.v[3] = (this.v[3] ^ d) >>> 0;
    this.v[4] = (this.v[4] ^ e) >>> 0;
    this.v[5] = (this.v[5] ^ f) >>> 0;
    this.v[6] = (this.v[6] ^ g) >>> 0;
    this.v[7] = (this.v[7] ^ h) >>> 0;
  }
}

/** 一次性计算（小文件用） */
export function sm3Hex(data: Uint8Array): string {
  return new SM3().update(data).digest();
}

/**
 * 校验 SM3 摘要格式（64 位小写十六进制）。
 * 用于完整性判断：任何异常结果都必须能被立即识别，而不是当作正常哈希用下去。
 */
export function isValidSm3Hex(hash: unknown): boolean {
  return typeof hash === 'string' && /^[0-9a-f]{64}$/.test(hash);
}
